import React, { useState, useEffect } from "react"
import { Box, Typography } from "@mui/material"

const DrumPad = () => {
  const [audioElements, setAudioElements] = useState({})
  const [activeToken, setActiveToken] = useState(null)
  const [slapToggle, setSlapToggle] = useState(true)
  const [toneToggle, setToneToggle] = useState(true)

const drumButtons = [
  {
    key: "bass",
    label: "X",
    normalColor: "#FDB93D",
    activeColor: "#FFD966",
    top: "30%",
    left: "50%",
    icon: "text",
  },
  {
    key: "tone-left",
    label: "●",          // was ▬
    normalColor: "#0D8FC7",
    activeColor: "#4FC3F7",
    bottom: "2%",
    left: "37.5%",
    icon: "dot",         // was bar
  },
  {
    key: "tone-right",
    label: "●",          // was ▬
    normalColor: "#0D8FC7",
    activeColor: "#4FC3F7",
    bottom: "2%",
    left: "62.5%",    
    icon: "dot",         // was bar
  },
  {
    key: "slap-left",
    label: "▬",          // was ●
    normalColor: "#F32718",
    activeColor: "#FF7260",
    top: "57.5%",
    left: "32.5%",
    icon: "bar",         // was dot
  },
  {
    key: "slap-right",
    label: "▬",          // was ●
    normalColor: "#F32718",
    activeColor: "#FF7260",
    top: "57.5%",
    left: "67.5%",
    icon: "bar",         // was dot
  },
]

const infoButtons = [
  { label: "X", color: "#FDB93D", name: "BASS (A key)", desc: "Low pitch" },
  { label: "●", color: "#0D8FC7", name: "TONE (F key)", desc: "Medium pitch" }, 
  { label: "▬", color: "#F32718", name: "SLAP (S key)", desc: "High pitch" },    
]

  const drumAudioClips = [
    {
      name: "High_Conga",
      key: "slap",
      src: "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_slap_closed.mp3",
    },
    {
      name: "Medium_Conga",
      key: "tone",
      src: "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_high.mp3",
    },
    {
      name: "Low_Conga",
      key: "bass",
      src: "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_low.mp3",
    },
  ]

  useEffect(() => {
    const sounds = {}
    drumAudioClips.forEach((clip) => {
      const audio = new Audio(clip.src)
      sounds[clip.key] = audio
    })
    setAudioElements(sounds)
    return () => {
      Object.values(sounds).forEach((audio) => {
        audio.pause()
        audio.src = ""
      })
    }
  }, [])

  const playSound = (soundType) => {
    const baseType = soundType.includes("tone") ? "tone" : soundType.includes("slap") ? "slap" : soundType
    if (!audioElements[baseType]) return
    setActiveToken(soundType)
    setTimeout(() => setActiveToken(null), 300)
    audioElements[baseType].pause()
    audioElements[baseType].currentTime = 0
    audioElements[baseType].play().catch((error) => {
      console.error("Error playing sound:", error)
    })
  }

  const handleKeyDown = (e) => {
    if (["a", "A"].includes(e.key)) {
      playSound("bass")
    }
    if (["f", "F"].includes(e.key)) {
      const nextTone = toneToggle ? "tone-left" : "tone-right"
      setToneToggle(!toneToggle)
      playSound(nextTone)
    }
    if (["s", "S"].includes(e.key)) {
      const nextSlap = slapToggle ? "slap-left" : "slap-right"
      setSlapToggle(!slapToggle)
      playSound(nextSlap)
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [audioElements, toneToggle, slapToggle])

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Drum Pad Instructions
        </Typography>
        <Typography variant="body1">
          Click on the colored buttons or use keyboard keys to play different drum sounds:
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        <Box sx={{ width: { xs: "370px", sm: "380px", md: "420px" }, height: { xs: "370px", sm: "340px", md: "420px" }, position: "relative", mb: 1.5 }}>
          <Box sx={{ width: "100%", height: "100%", position: "absolute" }}>
            <svg width="100%" height="100%" viewBox="0 0 380 340" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <path d="M190 20C193 20 196 21 198 24L360 300C363 305 362 312 358 316C354 320 350 320 345 320H35C30 320 26 320 22 316C18 312 17 305 20 300L182 24C184 21 187 20 190 20Z" fill="#222222" stroke="#333333" strokeWidth="5" />
            </svg>
          </Box>

          {drumButtons.map(({ key, label, normalColor, activeColor, top, bottom, left, icon }) => (
            <Box
              key={key}
              sx={{
                position: "absolute",
                ...(top ? { top } : { bottom }),
                left,
                transform: "translate(-50%, -50%)",
                width: { xs: "60px", sm: "65px", md: "70px" },
                height: { xs: "60px", sm: "65px", md: "70px" },
                borderRadius: "50%",
                bgcolor: activeToken === key ? activeColor : normalColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: activeToken === key ? `0 0 12px 6px ${activeColor}90` : "0 4px 8px rgba(0,0,0,0.3)",
                border: "3px solid white",
                zIndex: 2,
                "&:hover": {
                  transform: "translate(-50%, -50%) scale(1.05)",
                  boxShadow: "0 6px 12px rgba(0,0,0,0.4)",
                },
                "&:active": {
                  transform: "translate(-50%, -50%) scale(0.95)",
                },
              }}
              onClick={() => playSound(key)}
            >
              {icon === "text" ? (
                <Typography sx={{ color: "white", fontWeight: "900", fontSize: { xs: "28px", sm: "30px", md: "32px" }, textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}>
                  {label}
                </Typography>
              ) : icon === "bar" ? (
                <Box sx={{ width: "60%", height: "6px", backgroundColor: "white", borderRadius: "3px", boxShadow: "1px 1px 2px rgba(0,0,0,0.5)" }} />
              ) : (
                <Box sx={{ width: "16px", height: "16px", backgroundColor: "white", borderRadius: "50%", boxShadow: "1px 1px 3px rgba(0,0,0,0.5)" }} />
              )}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
          {infoButtons.map(({ label, color, name, desc }) => (
            <Box key={name} sx={{ textAlign: "center", color: "white" }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", mb: 1, border: "2px solid white" }}>
                <Typography sx={{ color: "white", fontWeight: "900", fontSize: "22px" }}>{label}</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>{name}</Typography>
              <Typography variant="body2" sx={{ display: "block" }}>{desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default DrumPad